import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Nav, NavLink, NavItem } from '@/components/ui-compat'
import { cn } from '@/lib/utils'
import {
  ListChecks,
  CheckSquare,
  Paperclip,
  Loader2,
  CheckCircle2,
  Plus,
  X,
} from 'lucide-react'

import { useModuleActions } from '../../../hooks/useMyPermissions'
import * as Query from '../../../api/gql/query'
import * as Mutation from '../../../api/gql/mutation'
import AgendaListManager from './AgendaListManager'
import ActionItemsStats from './ActionItemsStats'
import ActionItemsTable from './ActionItemsTable'
import ActionItemModal from './ActionItemModal'
import MeetingAttachmentsPanel from './MeetingAttachmentsPanel'

interface Props {
  isOpen: boolean
  onClose: () => void
  meetingId: string
}

const MeetingNotesDialog: React.FC<Props> = ({ isOpen, onClose, meetingId }) => {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<'agenda' | 'actions' | 'attachments'>(
    'agenda'
  )
  const [agendaSaving, setAgendaSaving] = useState(false)
  const [actionModalOpen, setActionModalOpen] = useState(false)
  const [editingActionId, setEditingActionId] = useState<string | null>(null)

  const { data, loading, refetch } = useQuery(Query.MEETING, {
    variables: { id: meetingId },
    skip: !meetingId || !isOpen,
    fetchPolicy: 'cache-first',
  })

  const meeting = data?.meeting
  const projectId = meeting?.project?.id
  const { canCreate, canEdit, canDelete } = useModuleActions('MEETINGS', projectId)
  const { data: projectData } = useQuery(Query.PROJECT, {
    variables: { id: projectId },
    skip: !projectId,
  })
  const users = (projectData?.project?.users || []).map((pu: any) => ({
    id: pu.user.id,
    name: pu.user.name,
  }))

  const [deleteActionItem] = useMutation(Mutation.DELETE_ACTION_ITEM, {
    refetchQueries: ['meeting'],
  })

  const handleDeleteAction = async (id: string) => {
    try {
      await deleteActionItem({ variables: { id } })
      toastr.success(t('Common_success'), t('ActionItem_deleted'))
    } catch (e: any) {
      toastr.error(t('Common_error'), e.message)
    }
  }

  const isAnySaving = agendaSaving

  // Attachments tab aggregates meeting-level files and per-agenda-item files.
  const attachmentCount =
    (meeting?.files?.length || 0) +
    (meeting?.agendaItems || []).reduce(
      (sum: number, a: any) => sum + (a.files?.length || 0),
      0
    )

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <DialogContent hideClose className="max-w-5xl h-[99vh] max-h-[99vh] flex flex-col overflow-hidden p-0 gap-0 sm:rounded-lg">
        <DialogClose className="absolute right-3 top-3 z-10 inline-flex h-10 w-10 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none">
          <X className="h-5 w-5" />
          <span className="sr-only">{t('Common_close')}</span>
        </DialogClose>
        <DialogHeader className="shrink-0 border-b pl-6 pr-14 py-3 flex-row flex-wrap !space-y-0 items-center gap-x-4 gap-y-2">
          <DialogTitle className="flex-1 min-w-[240px] flex items-baseline gap-2 truncate">
            <span className="shrink-0">{t('Meeting_notes_dialog_title')}</span>
            {meeting && (
              <span className="text-sm text-muted-foreground font-normal truncate">
                · {meeting.displayNumber} · {meeting.title}
              </span>
            )}
          </DialogTitle>

          <Nav tabs className="shrink-0">
            <NavItem>
              <NavLink
                className={cn('flex items-center gap-2', { active: activeTab === 'agenda' })}
                onClick={() => setActiveTab('agenda')}
              >
                <ListChecks className="h-4 w-4" />
                {t('Meeting_notes_tab_agenda')}
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={cn('flex items-center gap-2', { active: activeTab === 'actions' })}
                onClick={() => setActiveTab('actions')}
              >
                <CheckSquare className="h-4 w-4" />
                {t('Meeting_notes_tab_actions')}
                {meeting?.actionItems?.length > 0 && (
                  <span className="text-xs text-muted-foreground">
                    ({meeting.actionItems.length})
                  </span>
                )}
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={cn('flex items-center gap-2', { active: activeTab === 'attachments' })}
                onClick={() => setActiveTab('attachments')}
              >
                <Paperclip className="h-4 w-4" />
                {t('Meeting_notes_tab_attachments')}
                {attachmentCount > 0 && (
                  <span className="text-xs text-muted-foreground">
                    ({attachmentCount})
                  </span>
                )}
              </NavLink>
            </NavItem>
          </Nav>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
          {loading && !meeting ? (
            <div className="space-y-3">
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          ) : !meeting ? (
            <p className="text-sm text-muted-foreground p-4">
              {t('Meeting_not_found')}
            </p>
          ) : activeTab === 'agenda' ? (
            <AgendaListManager
              meetingId={meetingId}
              agendaItems={meeting.agendaItems || []}
              users={users}
              onSavingChange={setAgendaSaving}
              canEdit={canEdit}
              canDelete={canDelete}
            />
          ) : activeTab === 'actions' ? (
            <div className="space-y-4">
              <ActionItemsStats items={meeting.actionItems || []} />

              {canCreate && (
                <div className="flex items-center justify-end">
                  <Button
                    size="sm"
                    onClick={() => {
                      setEditingActionId(null)
                      setActionModalOpen(true)
                    }}
                  >
                    <Plus className="h-4 w-4" />
                    {t('ActionItem_new_button')}
                  </Button>
                </div>
              )}

              {(meeting.actionItems || []).length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">
                  {t('ActionItem_empty_hint')}
                </p>
              ) : (
                <ActionItemsTable
                  items={meeting.actionItems}
                  onEdit={
                    canEdit
                      ? (id) => {
                          setEditingActionId(id)
                          setActionModalOpen(true)
                        }
                      : undefined
                  }
                  onDelete={canDelete ? handleDeleteAction : undefined}
                />
              )}
            </div>
          ) : activeTab === 'attachments' ? (
            <MeetingAttachmentsPanel
              meetingId={meetingId}
              files={meeting.files || []}
              agendaItems={meeting.agendaItems || []}
              canEdit={canEdit}
            />
          ) : null}
        </div>

        <div className="shrink-0 border-t px-6 py-1.5 text-xs text-muted-foreground flex items-center gap-1.5">
          {isAnySaving ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {t('Meeting_autosave_saving')}
            </>
          ) : (
            <>
              <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
              {t('Meeting_autosave_saved')}
            </>
          )}
        </div>

        {actionModalOpen && (
          <ActionItemModal
            isOpen={actionModalOpen}
            onClose={(saved) => {
              setActionModalOpen(false)
              setEditingActionId(null)
              if (saved) refetch()
            }}
            actionItemId={editingActionId}
            meetingId={editingActionId ? null : meetingId}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

export default MeetingNotesDialog
